import { Component, Event, EventEmitter, h, Host, Prop, Watch } from "@stencil/core";
import { getUniqueId } from "../../utils/unique-id";

/**
 * @slot - contents of the textarea element (alternative for the value prop)
 * @slot (description) - an additional description for the user, additional to the label
 */

@Component({
    tag: "ux-textarea-field",
    styleUrl: "textarea-field.scss",
    shadow: false
})
export class TextareaField {
    private textareaElement: HTMLTextAreaElement;

    /** label for the the textarea field */
    @Prop() readonly label: string = "";
    /** additional info label for  the input field */
    @Prop() readonly labelInfo: string = "";
    /** value of the textarea element
     * default value must be undefined - otherwise the default slot does not work as expected
     * */

    @Prop({ mutable: true }) value: string;
    /** textarea id attribute */
    /* eslint-disable-next-line */
    @Prop() textareaId: string = "";
    /** name of the textarea field */
    @Prop() readonly name: string = "";
    /** placeholder for the textarea field */
    @Prop() readonly placeholder: string = "";
    /** indicates if the textarea field is disabled */
    @Prop() readonly disabled: boolean = false;
    /** indicates if the textarea field is required  */
    @Prop() readonly required: boolean = false;
    /** indicates if the textarea field has an error state  */
    @Prop() readonly error: boolean = false;
    /** error message that is rendered if component has an error */
    @Prop() readonly errorMsg: string = "";
    /** autocomplete attribute, will be set on the input element. Refer to HTML standards for allowed values */
    // @ts-expect-error Check for typing
    @Prop() readonly autocomplete: string = null;

    @Watch("textareaId")
    setTextareaId() {
        this.textareaId = this.textareaId || getUniqueId();
    }

    componentWillLoad() {
        this.setTextareaId();
    }

    /** Emitted when a keyboard input occurred. */
    @Event() uxInput!: EventEmitter;

    /** Emitted when the value has changed. */
    @Event() uxChange!: EventEmitter;

    /** textareaField event, includes name, value and textareaFieldId */
    @Event({
        eventName: "uxTextareaFieldChange",
        composed: true,
        cancelable: true,
        bubbles: true
    })
    textareaFieldChange!: EventEmitter<{
        name: string;
        value: string;
        textareaFieldId: string;
    }>;

    private handleInput = (e: KeyboardEvent) => {
        this.value = this.textareaElement.value;
        this.uxInput.emit();
        this.textareaFieldChange.emit({
            value: (e.target as HTMLTextAreaElement).value,
            name: this.name,
            textareaFieldId: this.textareaId
        });
    };

    private handleChange = () => {
        this.value = this.textareaElement.value;
        this.uxChange.emit();
    };

    render() {
        const baseClass = "ux-textarea-field";

        const {
            label,
            labelInfo,
            value,
            textareaId,
            name,
            placeholder,
            disabled,
            required,
            error,
            errorMsg,
            handleChange,
            handleInput
        } = this;

        return (
            <Host class={baseClass}>
                <ux-input-label
                    required={required}
                    text={label}
                    labelInfo={labelInfo}
                    error={error}
                    errorMsg={errorMsg}
                    inputId={textareaId}
                />
                <slot name="description" />
                <textarea
                    class={{
                        [`ux-textarea`]: true,
                        [`ux-textarea--disabled`]: disabled,
                        [`ux-textarea--error`]: error
                    }}
                    id={textareaId}
                    name={name}
                    value={value}
                    placeholder={placeholder}
                    disabled={disabled}
                    required={required}
                    aria-invalid={error ? "true" : "false"}
                    // @ts-expect-error Check for typing
                    ref={(el) => (this.textareaElement = el)}
                    //@ts-expected-error Stencil Bug https://github.com/ionic-team/stencil/issues/2882
                    autocomplete={this.autocomplete}
                    // @ts-expect-error Check for typing
                    onInput={(e) => handleInput(e)}
                    // @ts-expect-error Check for typing
                    onChange={(e) => handleChange(e)}
                >
                    <slot />
                </textarea>
            </Host>
        );
    }
}
